import { useState, useEffect, useRef, useCallback } from 'react';
import { useStateSelector } from 'hooks';
import { NetIncomeDashboardModel } from 'models';
import { format, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import { serviceDateFormat } from 'utils';
import services from 'services';

import { NetIncomeAddonDetails } from 'components';

function calculateDefaultDatesValues(
  periodEndingDate: Date | null,
  activeTab: string
) {
  if (!periodEndingDate) {
    let d = new Date();
    return { startDate: d, endDate: d };
  }

  switch (activeTab) {
    case '0':
      return {
        startDate: startOfMonth(periodEndingDate),
        endDate: endOfMonth(periodEndingDate),
      };
    case '1':
      return {
        startDate: startOfYear(periodEndingDate),
        endDate: periodEndingDate,
      };
    default:
      return {
        startDate: new Date(),
        endDate: new Date(),
      };
  }
}

interface NetIncomeAddonState {
  activeTab: string;
  dashboard: NetIncomeDashboardModel;
  startDate: Date | null;
  endDate: Date | null;
  loading: boolean;
  refresh: boolean;
  error: boolean;
}

interface Props {
  periodEndingDate: Date | null;
}

const NetIncomeAddon: React.FC<Props> = ({ periodEndingDate }) => {
  const fetched = useRef(false);
  const { coopId } = useStateSelector((state) => ({
    coopId: state.settings.current_cooperative,
  }));
  const [state, setState] = useState<NetIncomeAddonState>({
    activeTab: '0',
    dashboard: {
      CostsIncome: 0,
      GrossIncome: 0,
      NetIncome: 0,
    },
    startDate: new Date(),
    endDate: new Date(),
    error: false,
    loading: false,
    refresh: false,
  });

  const fetchDashboardData = async (startDate: Date, endDate: Date) => {
    const dashboardData: NetIncomeDashboardModel = (
      await services.get(
        `/api/portfolios/${coopId}/dashboard/net-incomes?startDate=${format(
          startDate,
          serviceDateFormat
        )}&endDate=${format(endDate, serviceDateFormat)}`
      )
    ).data.Data;

    return dashboardData;
  };

  const refreshDashboardData = async (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    if (startDate && endDate) {
      try {
        setState((prevState) => ({ ...prevState, refresh: true }));

        const dashboardData = await fetchDashboardData(startDate, endDate);

        setState((prevState) => ({
          ...prevState,
          dashboard: dashboardData,
          refresh: false,
        }));
      } catch (err) {
        console.error(err);
        setState((prevState) => ({
          ...prevState,
          refresh: false,
          error: true,
        }));
      }
    }
  };

  useEffect(() => {
    (async function () {
      try {
        setState((prevState) => ({
          ...prevState,
          loading: true,
          error: false,
        }));

        const { startDate, endDate } = calculateDefaultDatesValues(
          periodEndingDate,
          state.activeTab
        );
        const dashboardData = await fetchDashboardData(startDate, endDate);

        setState((prevState) => ({
          ...prevState,
          loading: false,
          dashboard: dashboardData,
          startDate,
          endDate,
        }));
      } catch (err) {
        console.error(err);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: true,
        }));
      }
    })();

    /* eslint-disable-next-line */
  }, [coopId]);

  useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
    }
  }, [state.dashboard]);

  useEffect(() => {
    if (fetched.current) {
      (async function () {
        await refreshDashboardData(state.startDate, state.endDate);
      })();
    }

    /* eslint-disable-next-line */
  }, [state.startDate, state.endDate]);

  const handleChangeTab = useCallback(
    (id: string) => {
      const { startDate, endDate } = calculateDefaultDatesValues(
        periodEndingDate,
        id
      );

      setState((prevState) => ({
        ...prevState,
        startDate,
        endDate,
        activeTab: id,
      }));
    },
    [periodEndingDate]
  );

  const handleChangeDate = useCallback((date: Date | null, name?: string) => {
    if (name) {
      setState((prevState) => ({
        ...prevState,
        [name]: date,
      }));
    }
  }, []);

  return (
    <NetIncomeAddonDetails
      {...state}
      periodEndingDate={periodEndingDate}
      handleChangeTab={handleChangeTab}
      handleChangeDate={handleChangeDate}
    />
  );
};

export default NetIncomeAddon;
